import { safeMessageUrl } from './messageUrl';
import type { ContentBlock, InlineContent, MessageEntity, MessageText } from './messageMarkup';

export interface RichNode {
  type: string;
  text?: string;
  attrs?: { href?: string; language?: string; start?: number; id?: string; kind?: string; label?: string };
  marks?: { type: string; attrs?: { href?: string } }[];
  content?: RichNode[];
}
export interface RichDocument {
  type: 'doc';
  content: RichNode[];
}
const object = (v: unknown): Record<string, unknown> | null =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
const string = (v: unknown) => (typeof v === 'string' ? v : '');
const types = new Set([
  'doc',
  'paragraph',
  'text',
  'hardBreak',
  'blockquote',
  'bulletList',
  'orderedList',
  'listItem',
  'codeBlock',
  'mention',
]);
const marks = new Set(['bold', 'italic', 'underline', 'strike', 'code', 'link']);

/** Bounded schema, independent of the editing engine. Drops executable/unknown attributes. */
export function normalizeRichDocument(value: unknown): RichDocument | null {
  let count = 0,
    size = 0,
    failed = false;
  function visit(value: unknown, depth: number): RichNode | null {
    const n = object(value);
    if (!n || ++count > 3000 || depth > 16) {
      failed = true;
      return null;
    }
    const type = string(n.type),
      attrs = object(n.attrs) ?? {};
    if (!types.has(type)) {
      failed = true;
      return null;
    }
    const out: RichNode = { type };
    if (type === 'text') {
      out.text = string(n.text);
      size += out.text.length;
      if (size > 100000) {
        failed = true;
        return null;
      }
      if (Array.isArray(n.marks))
        out.marks = n.marks.slice(0, 8).flatMap((v) => {
          const m = object(v);
          if (!m || !marks.has(string(m.type))) return [];
          if (m.type === 'link') {
            const href = safeMessageUrl(string(object(m.attrs)?.href));
            return href ? [{ type: 'link', attrs: { href } }] : [];
          }
          return [{ type: string(m.type) }];
        });
    }
    if (type === 'codeBlock') out.attrs = { language: string(attrs.language).slice(0, 40) };
    if (type === 'orderedList')
      out.attrs = {
        start:
          typeof attrs.start === 'number' &&
          Number.isSafeInteger(attrs.start) &&
          attrs.start > 0 &&
          attrs.start <= 9999
            ? attrs.start
            : 1,
      };
    if (type === 'mention')
      out.attrs = {
        id: string(attrs.id).slice(0, 128),
        kind: attrs.kind === 'channel' ? 'channel' : 'user',
        label: string(attrs.label).slice(0, 200),
      };
    if (Array.isArray(n.content))
      out.content = n.content.slice(0, 3001).flatMap((c) => {
        const child = visit(c, depth + 1);
        return child ? [child] : [];
      });
    return out;
  }
  const root = visit(value, 0);
  return !failed && root?.type === 'doc' ? { type: 'doc', content: root.content ?? [] } : null;
}
function plain(n: RichNode): string {
  if (n.type === 'text') return n.text ?? '';
  if (n.type === 'hardBreak') return '\n';
  if (n.type === 'mention')
    return (n.attrs?.kind === 'channel' ? '#' : '@') + (n.attrs?.label || n.attrs?.id || '');
  const separator = ['doc', 'bulletList', 'orderedList', 'listItem', 'blockquote'].includes(n.type)
    ? '\n'
    : '';
  return (n.content ?? []).map(plain).join(separator);
}
export function richMessage(document: unknown, fallback = ''): MessageText {
  const normalized = normalizeRichDocument(document);
  return { format: 'rich', text: normalized ? plain(normalized) : fallback, document: normalized };
}
export function richDocumentFromText(text: string): RichDocument {
  return {
    type: 'doc',
    content: text
      .split(/\r?\n/u)
      .map((line) => ({ type: 'paragraph', content: line ? [{ type: 'text', text: line }] : [] })),
  };
}
export function richContentBlocks(
  document: unknown,
  entities: readonly MessageEntity[],
): ContentBlock[] | null {
  const root = normalizeRichDocument(document);
  if (!root) return null;
  function inline(nodes: readonly RichNode[]): InlineContent[] {
    return nodes.flatMap((n): InlineContent[] => {
      if (n.type === 'hardBreak') return [{ kind: 'text', text: '\n' }];
      if (n.type === 'mention') {
        const entity = entities.find((e) => e.id === n.attrs?.id && e.kind === n.attrs?.kind);
        return entity ? [{ kind: 'entity', entity }] : [{ kind: 'text', text: plain(n) }];
      }
      if (n.type !== 'text') return inline(n.content ?? []);
      let result: InlineContent = { kind: 'text', text: n.text ?? '' };
      if (n.marks?.some((m) => m.type === 'code')) return [{ kind: 'code', text: n.text ?? '' }];
      for (const mark of [...(n.marks ?? [])].reverse()) {
        if (mark.type === 'link' && mark.attrs?.href)
          result = { kind: 'link', href: mark.attrs.href, children: [result] };
        else {
          const kind = (
            { bold: 'strong', italic: 'emphasis', underline: 'underline', strike: 'strike' } as const
          )[mark.type as 'bold' | 'italic' | 'underline' | 'strike'];
          if (kind) result = { kind, children: [result] };
        }
      }
      return [result];
    });
  }
  function blocks(nodes: readonly RichNode[]): ContentBlock[] {
    return nodes.flatMap((n): ContentBlock[] => {
      if (n.type === 'codeBlock')
        return [
          { kind: 'code', text: (n.content ?? []).map(plain).join(''), language: n.attrs?.language ?? '' },
        ];
      if (n.type === 'bulletList' || n.type === 'orderedList')
        return [
          {
            kind: 'list',
            ordered: n.type === 'orderedList',
            start: n.attrs?.start ?? 1,
            items: (n.content ?? []).map((item) => blocks(item.content ?? [])),
          },
        ];
      if (n.type === 'blockquote')
        return [
          {
            kind: 'quote',
            blocks: blocks(n.content ?? []),
            children: inline(
              (n.content ?? []).flatMap((p, i) =>
                i ? [{ type: 'hardBreak' }, ...(p.content ?? [])] : (p.content ?? []),
              ),
            ),
          },
        ];
      if (n.type === 'paragraph') return [{ kind: 'paragraph', children: inline(n.content ?? []) }];
      return blocks(n.content ?? []);
    });
  }
  return blocks(root.content);
}
